"use client";

import { cn } from "@frontend/shared/lib/utils";
import { motion, useAnimation } from "framer-motion";
import { useCallback, useRef } from "react";
import { useId } from "react";

interface InsuranceToggleProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}

const TRACK_WIDTH = 80;
const TRACK_HEIGHT = 40;
const KNOB_SIZE = 36;
const KNOB_PADDING = 2;
const KNOB_TRAVEL = TRACK_WIDTH - KNOB_SIZE - KNOB_PADDING * 2;

const springTransition = {
  type: "spring" as const,
  stiffness: 400,
  damping: 22,
  mass: 0.8,
};

const HeartbeatSvg = ({ visible }: { visible: boolean }) => {
  const uid = useId();
  const id = `ins-tog-${uid.replace(/:/g, "")}`;
  return (
    <motion.svg
      role="img"
      aria-label="Insurance heartbeat icon"
      viewBox="0 0 32 32"
      width={KNOB_SIZE - 4}
      height={KNOB_SIZE - 4}
      fill="none"
      initial={false}
      animate={{
        opacity: visible ? 1 : 0,
        scale: visible ? 1 : 0.3,
      }}
      transition={{
        opacity: { duration: 0.15 },
        scale: { type: "spring", stiffness: 500, damping: 18 },
      }}
      style={{ position: "absolute" }}
    >
      <defs>
        <clipPath id={`${id}-c`}>
          <circle cx="16" cy="16" r="16" />
        </clipPath>
        <linearGradient
          id={`${id}-g1`}
          x1="1"
          y1="16"
          x2="26"
          y2="16"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" stopOpacity="0" />
          <stop offset="0.19" stopColor="white" />
          <stop offset="0.8" stopColor="white" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id={`${id}-g2`}
          x1="12"
          y1="19"
          x2="32"
          y2="19"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.14" stopColor="white" stopOpacity="0" />
          <stop offset="0.18" stopColor="white" />
          <stop offset="0.8" stopColor="white" />
          <stop offset="0.97" stopColor="white" stopOpacity="0.03" />
        </linearGradient>
        <linearGradient
          id={`${id}-g3`}
          x1="-1"
          y1="22"
          x2="19"
          y2="22"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.13" stopColor="white" stopOpacity="0" />
          <stop offset="0.43" stopColor="white" />
          <stop offset="0.97" stopColor="white" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>

      <g clipPath={`url(#${id}-c)`}>
        <path
          d="M18.8663 5C20.0196 5 20.9395 5.51383 21.6313 6.24613C22.2944 6.94784 22.7807 7.87747 23.1646 8.85376C23.93 10.8 24.4432 13.3594 24.9468 15.7518C25.4665 18.22 25.9787 20.526 26.7226 22.2166C27.4962 23.9747 28.2597 24.5024 29 24.5024V27C26.5553 27 25.1704 25.09 24.3352 23.1913C23.4699 21.2249 22.9126 18.6557 22.406 16.2482C21.883 13.7651 21.4124 11.4488 20.7402 9.73831C20.4051 8.88623 20.0592 8.29232 19.7142 7.9276C19.3984 7.59313 19.1346 7.49761 18.8666 7.49761C18.5986 7.49761 18.3515 7.58966 18.0524 7.91499C17.7218 8.27436 17.3916 8.86385 17.0752 9.71656C16.439 11.4302 16.0142 13.7431 15.5144 16.2366C15.0311 18.6491 14.4767 21.2318 13.5551 23.2143C12.6417 25.1792 11.1435 27 8.58922 27H4V24.5024H8.58922C9.56217 24.5024 10.4019 23.8855 11.1887 22.1936C11.9672 20.5194 12.4751 18.2264 12.9687 15.7634C13.4458 13.3812 13.9125 10.8186 14.6338 8.87551C14.9961 7.89986 15.4614 6.96581 16.1117 6.25874C16.7937 5.5173 17.7097 5 18.8666 5H18.8663Z"
          fill={`url(#${id}-g1)`}
          opacity={0.85}
        />
        <g opacity={0.55}>
          <path
            d="M26.062 11.1158C27.9345 11.1158 28.9689 12.5552 29.5603 13.854C30.1657 15.1833 30.5665 16.9163 30.9414 18.4537C31.3373 20.0769 31.7103 21.5127 32.2373 22.547C32.7579 23.5688 33.2305 23.8342 33.7168 23.8342H34.9991V26.3992H33.7168C31.7974 26.3992 30.6468 25.0751 29.952 23.7119C29.2638 22.3615 28.8287 20.6176 28.4493 19.0614C28.0488 17.419 27.7063 15.9726 27.2258 14.9172C26.7312 13.8314 26.3365 13.6808 26.0617 13.6808C25.804 13.6808 25.4307 13.8081 24.9645 14.8923C24.5094 15.9509 24.2015 17.3925 23.8172 19.0474C23.4543 20.6108 23.0211 22.3696 22.2846 23.7374C21.5222 25.1531 20.2862 26.3989 18.2981 26.3989H15.8047V23.8339H18.2981C18.9747 23.8339 19.5047 23.4901 20.0266 22.5211C20.5743 21.5043 20.9437 20.0837 21.3189 18.4676C21.6728 16.9431 22.0383 15.205 22.6084 13.879C23.1678 12.5785 24.1723 11.1158 26.062 11.1158Z"
            fill={`url(#${id}-g2)`}
          />
          <path
            d="M10.4429 17.6781C11.8816 17.6781 12.9259 18.2226 13.6961 19.0465C14.3905 19.7895 14.8451 20.7545 15.2002 21.4642C15.5965 22.2554 15.9102 22.8261 16.3301 23.2273C16.6881 23.5694 17.1744 23.8339 18.0498 23.8339C18.7581 23.8339 19.3324 24.4083 19.3324 25.1166C19.3324 25.8249 18.7581 26.3989 18.0498 26.3989C16.5343 26.3989 15.4077 25.8939 14.5579 25.0816C13.7699 24.3286 13.2811 23.3603 12.9065 22.6125C12.4912 21.7831 12.2069 21.2094 11.8223 20.7979C11.5135 20.4677 11.1376 20.2427 10.4426 20.2427C9.74749 20.2427 9.41533 20.4625 9.14275 20.7711C8.78275 21.1786 8.53476 21.7371 8.12847 22.5898C7.76362 23.3555 7.27348 24.3403 6.42723 25.1056C5.52691 25.9198 4.33167 26.3989 2.72722 26.3989H-0.717673C-1.42601 26.3989 -2 25.8246 -2 25.1166C-2 24.4086 -1.42569 23.8339 -0.717673 23.8339H2.72722C3.77096 23.8339 4.3307 23.5432 4.70656 23.2033C5.13648 22.8145 5.44404 22.2599 5.81277 21.4868C6.13975 20.8008 6.56061 19.8202 7.22006 19.0734C7.96693 18.2278 8.99836 17.6777 10.4426 17.6777H10.4429V17.6781Z"
            fill={`url(#${id}-g3)`}
          />
        </g>
      </g>
    </motion.svg>
  );
};

const InsuranceToggle = ({
  checked,
  onCheckedChange,
  disabled = false,
  className,
}: InsuranceToggleProps) => {
  const bounceControls = useAnimation();
  const isAnimating = useRef(false);

  const handleToggle = useCallback(() => {
    if (disabled || isAnimating.current) return;
    isAnimating.current = true;

    // Fire the bounce
    bounceControls
      .start({
        scale: [1, 1.4, 0.92, 1.08, 1],
        transition: {
          duration: 0.5,
          times: [0, 0.2, 0.4, 0.65, 1],
          ease: "easeOut",
        },
      })
      .then(() => {
        isAnimating.current = false;
      });

    onCheckedChange(!checked);
  }, [disabled, checked, onCheckedChange, bounceControls]);

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label="Toggle insurance"
      data-testid="kiosk-toggle"
      disabled={disabled}
      onClick={handleToggle}
      className={cn(
        "relative inline-flex shrink-0 cursor-pointer items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      style={{
        width: TRACK_WIDTH,
        height: TRACK_HEIGHT,
        padding: KNOB_PADDING,
      }}
    >
      {/* Track */}
      <motion.div
        className="absolute inset-0 rounded-full"
        initial={false}
        animate={{
          backgroundColor: checked ? "#C0E7EB" : "#D3D3D3",
        }}
        transition={{ duration: 0.3 }}
      />

      {/* Knob container - handles slide position */}
      <motion.div
        className="relative z-10"
        initial={false}
        animate={{ x: checked ? KNOB_TRAVEL : 0 }}
        transition={springTransition}
      >
        {/* Knob visual - handles bounce + color (no key remount!) */}
        <motion.div
          className="rounded-full flex items-center justify-center overflow-hidden"
          style={{
            width: KNOB_SIZE,
            height: KNOB_SIZE,
          }}
          initial={false}
          animate={{
            backgroundColor: checked ? "#00A3FF" : "#FFFFFF",
            boxShadow: checked
              ? "0 2px 10px rgba(0, 163, 255, 0.45), 0 1px 3px rgba(0,0,0,0.15)"
              : "0 2px 8px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.1)",
          }}
          transition={{
            backgroundColor: { duration: 0.25 },
            boxShadow: { duration: 0.3 },
          }}
        >
          {/* Bounce wrapper - imperatively animated, never remounts */}
          <motion.div
            className="flex items-center justify-center"
            style={{
              width: KNOB_SIZE,
              height: KNOB_SIZE,
            }}
            animate={bounceControls}
          >
            <HeartbeatSvg visible={checked} />
          </motion.div>
        </motion.div>
      </motion.div>
    </button>
  );
};

export { InsuranceToggle };
export type { InsuranceToggleProps };
